import React, { useContext, useState } from "react";

import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";

import { GrantSource } from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import { provisionAICreditPack } from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import DatePicker from "components/shared/date-picker";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

const SUPPORT_PACK_DEFAULT_CREDITS = 300;
const CREDIT_PACK_MAX_CREDITS = 50000;
const CREDIT_PACK_MIN_CREDITS = 1;

const ALLOWED_GRANT_SOURCES = [
  { value: GrantSource.SUPPORT, label: "Support" },
  { value: GrantSource.WELCOME, label: "Welcome" },
];

interface ProvisionAICreditPackProps {
  open: boolean;
  orgId: string;
  regionCode: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ProvisionAICreditPack = ({
  open,
  orgId,
  regionCode,
  onClose,
  onSuccess,
}: ProvisionAICreditPackProps) => {
  const { showError } = useContext(SnackbarContext);

  const [grantSource, setGrantSource] = useState<GrantSource>(
    ALLOWED_GRANT_SOURCES[0].value,
  );
  const [amount, setAmount] = useState(SUPPORT_PACK_DEFAULT_CREDITS);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const isAmountInvalid =
    amount < CREDIT_PACK_MIN_CREDITS || amount > CREDIT_PACK_MAX_CREDITS;
  const isFormComplete = !isAmountInvalid && !!expirationDate;
  const isConfirmDisabled = !isFormComplete || !isConfirmed || isProvisioning;

  const handleConfirm = async () => {
    if (!expirationDate) return;

    setIsProvisioning(true);
    try {
      await provisionAICreditPack(
        {
          organisationId: orgId,
          creditPack: {
            amount,
            grantSource,
            effectiveTo: expirationDate,
          },
        },
        callOptionsHeaders(regionCode, orgId),
      );
      onSuccess();
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsProvisioning(false);
    }
  };

  const handleClose = () => {
    setGrantSource(ALLOWED_GRANT_SOURCES[0].value);
    setAmount(SUPPORT_PACK_DEFAULT_CREDITS);
    setExpirationDate(null);
    setIsConfirmed(false);
    onClose();
  };

  return (
    <ConfirmDialog
      title="Provision AI Credit Pack"
      open={open}
      setOpen={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      onConfirm={handleConfirm}
      onCancel={handleClose}
      cancelBtnLabel="Cancel"
      confirmBtnLabel="Provision"
      disabled={isConfirmDisabled}
      disableCloseOnConfirm
      disableBackgroundClick={isProvisioning}
    >
      <Stack spacing={2} sx={{ mt: 1, width: "100%" }}>
        <Alert severity="warning">
          This will immediately grant additional AI credits to the organisation.
          Support and Welcome packs require an expiration date.
        </Alert>

        <FormControl fullWidth size="small">
          <InputLabel id="grant-source-label">Pack type</InputLabel>
          <Select
            labelId="grant-source-label"
            label="Pack type"
            value={grantSource}
            onChange={(e) => setGrantSource(e.target.value as GrantSource)}
            data-testid="grant-source-select"
          >
            {ALLOWED_GRANT_SOURCES.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Credits"
          type="number"
          size="small"
          fullWidth
          value={amount}
          onChange={(e) => setAmount(Number(e.target.value))}
          inputProps={{
            min: CREDIT_PACK_MIN_CREDITS,
            max: CREDIT_PACK_MAX_CREDITS,
            "data-testid": "credit-amount-input",
          }}
          error={isAmountInvalid}
          helperText={
            isAmountInvalid
              ? `Value must be between ${CREDIT_PACK_MIN_CREDITS} and ${CREDIT_PACK_MAX_CREDITS.toLocaleString()}`
              : `Max ${CREDIT_PACK_MAX_CREDITS.toLocaleString()} credits`
          }
        />

        <Stack spacing={0.5}>
          <DatePicker
            label="Expiration date"
            minDate={new Date()}
            onChange={(date: Date | null) => setExpirationDate(date)}
            size="small"
          />
        </Stack>

        {isFormComplete && (
          <Alert
            severity="info"
            icon={false}
            sx={{
              "& .MuiAlert-message": { width: "100%", py: 0 },
            }}
          >
            <FormControlLabel
              sx={{ m: 0, alignItems: "center" }}
              control={
                <Checkbox
                  checked={isConfirmed}
                  onChange={(e) => setIsConfirmed(e.target.checked)}
                  data-testid="provision-confirmation-checkbox"
                  size="small"
                  sx={{ py: 0.5, mr: 0.5 }}
                />
              }
              label="I confirm that the above details are correct."
            />
          </Alert>
        )}
      </Stack>
    </ConfirmDialog>
  );
};

export default ProvisionAICreditPack;
